"use client";

import * as React from "react";
import { Alignment, Fit, Layout, useRive } from "@rive-app/react-canvas";
import { cn } from "@marketing-sites/utils/cn";
import Image from "next/image";
import { useCarousel } from "./carousel.context";
import type { RivePlaceholder, RiveFit, Align } from "../../components/rive";
import {
  FIT_TO_OBJECT_FIT,
  ALIGN_TO_OBJECT_POSITION,
} from "../../components/rive/object-fit";

export type CarouselRiveProps = {
  /** Slide index this Rive lives in — same pattern as `Carousel.Thumbnail`. */
  index: number;
  src: string;
  fit?: RiveFit;
  align?: Align;
  /** For state machine animations. Skips `reset()` on each slide selection. */
  stateMachineName?: string;
  /** Render a named artboard from the `.riv` instead of the file's default. */
  artboard?: string;
  className?: string;
  innerClassName?: string;
  placeholder?: RivePlaceholder;
  /**
   * Render this image instead of the animation; the player never mounts and no
   * `.riv` is fetched. See `staticImage` on `@marketing-sites/ui/rive`.
   */
  staticImage?: RivePlaceholder;
};

/** Minimal Rive instance shape used by the carousel sync hook. */
interface CarouselRiveInstance {
  reset: (params?: { artboard?: string }) => void;
  play: () => void;
  stop: () => void;
}

/**
 * Shared carousel-sync effect. Wires Rive playback to Embla's
 * select/autoplay events so the animation plays from frame 0
 * when its slide is selected.
 */
export function useCarouselRive(
  index: number,
  rive: CarouselRiveInstance | null,
  stateMachineName?: string,
  artboard?: string,
) {
  const { emblaApi } = useCarousel();

  React.useEffect(() => {
    if (!emblaApi || !rive) return;

    const autoplay = emblaApi.plugins()?.autoplay;
    const isSelected = () => emblaApi.selectedScrollSnap() === index;
    // If no autoplay plugin (manual carousel), treat as always playing.
    const isPlaying = () => (autoplay ? autoplay.isPlaying() : true);

    const playRive = () => {
      // timeline: always restart from frame 0. `reset()` with no args reverts
      // to the file's default artboard, so pass the selected one. Without
      // `artboard` this is `reset(undefined)`, identical to before.
      if (!stateMachineName) rive.reset(artboard ? { artboard } : undefined);
      rive.play();
    };

    // Sync immediately once rive is loaded.
    if (isSelected() && isPlaying()) {
      playRive();
    }

    const onSelect = () => {
      if (isSelected() && isPlaying()) {
        playRive();
      } else if (!isSelected()) {
        rive.stop();
      }
    };
    const onAutoplayPlay = () => {
      if (isSelected()) playRive();
    };
    const onAutoplayStop = () => rive.stop();

    emblaApi
      .on("select", onSelect)
      .on("autoplay:play", onAutoplayPlay)
      .on("autoplay:stop", onAutoplayStop);

    return () => {
      emblaApi
        .off("select", onSelect)
        .off("autoplay:play", onAutoplayPlay)
        .off("autoplay:stop", onAutoplayStop);
    };
  }, [emblaApi, rive, index, stateMachineName, artboard]);
}

/**
 * Placeholder image shown before a carousel Rive has loaded — and, with the
 * carousel-level load gate, while the carousel is still below the viewport.
 * Shared by both the Canvas2D and WebGL2 carousel Rive components.
 */
export function CarouselRivePlaceholder({
  placeholder,
  objectClassName = "object-cover object-center",
}: {
  placeholder?: RivePlaceholder;
  objectClassName?: string;
}) {
  if (!placeholder) return null;
  return (
    <div className="absolute inset-0 size-full overflow-hidden">
      <Image
        src={placeholder.src}
        alt={placeholder.alt}
        fill
        priority={placeholder.priority}
        loading={placeholder.priority ? "eager" : "lazy"}
        fetchPriority={placeholder.priority ? "high" : "auto"}
        className={cn("size-full", objectClassName)}
      />
    </div>
  );
}

/**
 * A still stands in for the whole animation, so it has to be framed the way the
 * animation would have been. The loading placeholder keeps its own cover/center
 * framing — it is only ever on screen mid-load.
 */
export function staticImageObjectClassName(fit?: RiveFit, align?: Align) {
  return cn(
    FIT_TO_OBJECT_FIT[fit ?? "Cover"],
    ALIGN_TO_OBJECT_POSITION[align ?? "Center"],
  );
}

/**
 * A Rive animation (Canvas2D) whose playback is driven by the carousel.
 *
 * - Plays from frame 0 when its slide is selected and autoplay is active.
 * - Stops when its slide is deselected or the carousel leaves the viewport.
 *
 * Must be rendered inside `<Carousel.Root>`. Set `<Carousel.Slide delay={ms}>`
 * to match the Rive animation's total duration so they stay in sync.
 *
 * Loading is gated on the carousel's `isInView` signal: `useRive` (which fetches
 * the `.riv`) lives in the inner component, which only mounts once the carousel
 * nears the viewport. Until then the placeholder / slide backdrop shows.
 */
export function Rive(props: CarouselRiveProps) {
  const { isInView } = useCarousel();

  if (props.staticImage || !isInView) {
    return (
      <div className={cn("relative size-full", props.className)}>
        <CarouselRivePlaceholder
          placeholder={props.staticImage ?? props.placeholder}
          objectClassName={
            props.staticImage
              ? staticImageObjectClassName(props.fit, props.align)
              : undefined
          }
        />
      </div>
    );
  }

  return <RiveInner {...props} />;
}

function RiveInner({
  index,
  src,
  fit = "Cover",
  align = "Center",
  stateMachineName,
  artboard,
  className,
  placeholder,
}: CarouselRiveProps) {
  const { RiveComponent, rive } = useRive({
    src,
    artboard,
    autoplay: false,
    stateMachines: stateMachineName ? [stateMachineName] : undefined,
    layout: new Layout({ fit: Fit[fit], alignment: Alignment[align] }),
  });

  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    if (rive && !isLoaded) {
      setIsLoaded(true);
    }
  }, [rive, isLoaded]);

  useCarouselRive(index, rive, stateMachineName, artboard);

  return (
    <div className={cn("relative size-full", className)}>
      {!isLoaded && <CarouselRivePlaceholder placeholder={placeholder} />}
      <RiveComponent className="size-full" />
    </div>
  );
}
